import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { pauseSmoothScroll } from "../motion/smoothScroll";

// 사진 크게 보기(확대보기). 어디서든 useLightbox().open(items, index, opener) 로 엽니다.
// items: [{ src, full, thumb, w, h, cap, meta }]
//  - full: 확대용 사진(없으면 src), w·h: 원본 픽셀(원본보다 크게 늘리지 않고, 받는 동안 자리를 잡아 둠)
//  - thumb: 큰 사진을 받는 동안 흐리게 먼저 보여 줄 작은 사진, cap: 설명, meta: 작은 보조 글
const LightboxContext = createContext({ open: () => {}, close: () => {} });

export function useLightbox() {
  return useContext(LightboxContext);
}

const pad2 = (n) => String(n).padStart(2, "0");

export function LightboxProvider({ children }) {
  const [state, setState] = useState({ isOpen: false, items: [], index: 0 });
  const openerRef = useRef(null);

  const open = useCallback((list, index = 0, opener = null) => {
    const items = list || [];
    openerRef.current = opener || document.activeElement;
    const n = items.length;
    setState({ isOpen: true, items, index: n ? ((index % n) + n) % n : 0 });
  }, []);

  const close = useCallback(() => {
    setState((s) => (s.isOpen ? { ...s, isOpen: false } : s));
    const opener = openerRef.current;
    if (opener && opener.focus) opener.focus();
  }, []);

  const show = useCallback((index) => {
    setState((s) => {
      const n = s.items.length;
      if (!n) return s;
      return { ...s, index: ((index % n) + n) % n };
    });
  }, []);

  const value = useMemo(() => ({ open, close }), [open, close]);

  return (
    <LightboxContext.Provider value={value}>
      {children}
      <Lightbox state={state} show={show} close={close} />
    </LightboxContext.Provider>
  );
}

function Lightbox({ state, show, close }) {
  const { isOpen, items, index } = state;
  const rootRef = useRef(null);
  const closeRef = useRef(null);
  const touchX = useRef(null);
  const [loadedSrc, setLoadedSrc] = useState(null);
  const item = items[index];
  const multi = items.length > 1;

  useEffect(() => {
    document.body.classList.toggle("ib-lb-open", isOpen);
    pauseSmoothScroll(isOpen);
    if (isOpen && closeRef.current) closeRef.current.focus();
    return () => {
      document.body.classList.remove("ib-lb-open");
      pauseSmoothScroll(false);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
      } else if (e.key === "ArrowRight") show(index + 1);
      else if (e.key === "ArrowLeft") show(index - 1);
      else if (e.key === "Tab" && rootRef.current) {
        const f = [...rootRef.current.querySelectorAll("button:not([hidden])")];
        const first = f[0];
        const last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, index, show, close]);

  const onBackdrop = (e) => {
    if (e.target === rootRef.current || e.target.classList.contains("ib-lb__stage")) close();
  };
  const onTouchStart = (e) => {
    touchX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) > 45) show(index + (dx < 0 ? 1 : -1));
  };

  const showImage = isOpen && item;
  const src = item ? item.full || item.src : null;
  return (
    <div
      ref={rootRef}
      className={"ib-lb" + (isOpen ? " is-open" : "")}
      role="dialog"
      aria-modal="true"
      aria-label="사진 크게 보기"
      onClick={onBackdrop}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="ib-lb__bar">
        <span className="ib-lb__count">{item ? `${pad2(index + 1)} / ${pad2(items.length)}` : ""}</span>
        <button ref={closeRef} type="button" className="ib-lb__close" aria-label="닫기" onClick={close}>
          ×
        </button>
      </div>
      <div className="ib-lb__stage">
        <button
          type="button"
          className="ib-lb__nav ib-lb__nav--prev"
          aria-label="이전 사진"
          hidden={!multi}
          onClick={() => show(index - 1)}
        >
          ‹
        </button>
        {showImage && item.w && item.h ? (
          // 원본 비율로 자리를 먼저 잡고(원본보다 크게 늘리지 않음), 흐린 썸네일 → 큰 사진 순서로 보여 준다
          <div className="ib-lb__frame" style={{ "--ar": item.w / item.h, "--w": `${item.w}px` }}>
            {item.thumb && <img className="ib-lb__ph" src={item.thumb} alt="" aria-hidden="true" />}
            <img
              key={src}
              className={"ib-lb__img" + (loadedSrc === src ? " is-loaded" : "")}
              src={src}
              alt={item.cap || ""}
              onLoad={() => setLoadedSrc(src)}
            />
          </div>
        ) : showImage ? (
          <img
            className="ib-lb__img"
            src={src}
            alt={item.cap || ""}
            // 원본보다 크게 늘리지 않는다
            style={item.w ? { maxWidth: `min(100%, ${item.w}px)` } : undefined}
          />
        ) : (
          <img className="ib-lb__img" alt="" />
        )}
        <button
          type="button"
          className="ib-lb__nav ib-lb__nav--next"
          aria-label="다음 사진"
          hidden={!multi}
          onClick={() => show(index + 1)}
        >
          ›
        </button>
      </div>
      <div className="ib-lb__cap" aria-live="polite">
        {item && (
          <>
            <span>{item.cap || ""}</span>
            {item.meta && <small>{item.meta}</small>}
          </>
        )}
      </div>
    </div>
  );
}
